import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Col, Menu, Row, Space } from 'antd';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import {
  ListWorkOrdersQuery,
  ListWorkOrdersResult,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { useWorkOrders } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';

import { getTableScrollX } from '@/utils/table';

import { PageProps } from '@/types/PageProps';
import WorkOrdersExtraFilters from '@/components/Movements/WorkOrders/List/WorkOrdersExtraFilters';
import useWorkOrdersColumns from '@/components/Movements/WorkOrders/List/useWorkOrdersColumns';
import CreateWorkOrderModal from '@/components/Movements/WorkOrders/Create/CreateWorkOrderModal';
import {
  AppPageHeader,
  DefaultMoreVertDropdown,
  DefaultRouteTabs,
  InfiniteTable,
} from '@/components/_common';
import { useDebounce, useDisclosure } from '@/hooks';
import WorkorderImportModal from '@/components/Movements/WorkOrders/WorkorderImport/WorkorderImportModal';
import { useHistory } from 'react-router-dom';
import WorkorderCompanySelectModal, {
  ReportTypeCompanySelect,
} from './WorkorderCompanySelectModal';
import WorkOrderChecklistEmptyModal from '@/components/Movements/WorkOrders/WorkOrderChecklistEmptyModal';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import WorkOrdersByWhatsappModal, {
  CustomerWorkorder,
} from '@/components/Movements/WorkOrders/List/WorkOrdersByWhatsappModal';
import { workorderRoutes } from './route';
import workorderOrderColumns from '@/utils/functions/workorderOrderColumns/workorderOrderColumns';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import usePageSearchHistory from '@/hooks/pageSearchHistory';

function WorkOrdersPage({ route }: PageProps) {
  const intl = useIntl();
  const [modalImportOpen, modalImportAction] = useDisclosure();
  const [modalSelectCompany, modalSelectCompanyAction] = useDisclosure(false);
  const [reportType, setReportType] = useState<ReportTypeCompanySelect>();
  const { columns, isFetching: isFetchingColumns } = useWorkOrdersColumns();
  const history = useHistory();
  const [modalChecklistOpened, modalChecklistAction] = useDisclosure();

  const [createModalVisible, setCreateModalVisible] = useState(false);

  const [modalCustomerSendOpen, modalCustomerSendAction] = useDisclosure(false);

  const [queryParams, setQueryParams] = useState<ListWorkOrdersQuery>({
    order: 1,
    ...convertParams<ListWorkOrdersQuery>({
      search: 'string',
      order: 'number',
      companiesIds: 'array-number',
      company: 'string',
      number: 'string',
      estimator: 'string',
      estimatedLeaveDate: 'string',
      customer: 'string',
      customerType: 'string',
      insurer: 'string',
      licensePlate: 'string',
      vehicle: 'string',
      entryDate: 'string',
      authorizationDate: 'string',
      closingDate: 'string',
      returnDate: 'string',
      status: 'array-string',
      type: 'array-string',
      cost: 'number',
      tagsId: 'array-number',
      statusId: 'array-number',
      fleet: 'string',
      isPortoBatteryWorkOrder: 'boolean',
      workOrderWithBatteryWarranty: 'boolean',
      complementOrigin: 'array-number',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { setLocation } = usePageSearchHistory();
  useEffect(setLocation, [queryDebounce]);

  const { data, isFetching, hasNextPage, fetchNextPage } =
    useWorkOrders(queryDebounce);

  const [hasWorkorderByWhatsapp, setHasWorkorderByWhatsapp] = useState(false);
  const [workordersSelected, setWorkordersSelected] = useState<
    ListWorkOrdersResult[]
  >([]);

  const workOrders = flatInfiniteData(data);

  const handleCreateModalVisible = () => {
    setCreateModalVisible(!createModalVisible);
  };

  const newRoute = {
    ...route,
    routes: [
      route,
      ...route.routes.filter((route) => workorderRoutes.includes(route.key)),
    ],
  };

  function cancelWorkorderByWhatsapp() {
    setWorkordersSelected([]);
    setHasWorkorderByWhatsapp(false);
  }

  const customerWorkorders = workordersSelected.reduce((acc, item) => {
    const customerFinded = acc.find(
      (accItem) => accItem.customer.id === item.customer.id,
    );

    if (customerFinded) {
      return acc.map((accItem) => ({
        ...accItem,
        workorders: [...accItem.workorders, item],
      }));
    }

    return [...acc, { customer: item.customer, workorders: [item] }];
  }, [] as CustomerWorkorder[]);

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <DefaultRouteTabs
        route={newRoute}
        persistSearchParams={false}
        permissions={route.permissions}
        extra={
          <Space align="center">
            <Button
              block
              type="primary"
              onClick={handleCreateModalVisible}
              disabled={
                !hasPermissions([Authorities.MOVIMENTATION_WORKORDER_ADD])
              }
            >
              <FormattedMessage id="workOrders.create" />
            </Button>

            <DefaultMoreVertDropdown>
              <Menu>
                <Menu.Item
                  key="blankWorkOrder"
                  onClick={() => {
                    setReportType('workOrder');
                    modalSelectCompanyAction.open();
                  }}
                >
                  <FormattedMessage id="workOrders.blankWorkOrder" />
                </Menu.Item>

                <Menu.Item
                  key="blankChecklist"
                  onClick={modalChecklistAction.open}
                >
                  <FormattedMessage id="workOrders.blankChecklist" />
                </Menu.Item>

                <Menu.Item
                  key="mechanicsRecord"
                  onClick={() => {
                    setReportType('mechanicsRecord');
                    modalSelectCompanyAction.open();
                  }}
                >
                  Folha do mecânico em Branco
                </Menu.Item>

                <Menu.Item
                  key="importWorkOrder"
                  onClick={() =>
                    history.push('/app/movements/workOrders/import')
                  }
                  disabled={
                    !hasPermissions([
                      Authorities.MOVIMENTATION_WORKORDER_IMPORT,
                    ])
                  }
                >
                  <FormattedMessage id="workOrders.importWorkOrder" />
                </Menu.Item>

                <Menu.Item
                  key="workordersByWhatsapp"
                  onClick={() => setHasWorkorderByWhatsapp(true)}
                >
                  Orçamentos por Whatsapp
                </Menu.Item>

                <Menu.Item
                  key="receipt"
                  onClick={() => {
                    setReportType('receipt-template');
                    modalSelectCompanyAction.open();
                  }}
                >
                  Recibo em branco
                </Menu.Item>
              </Menu>
            </DefaultMoreVertDropdown>
          </Space>
        }
      >
        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, search: value }))
          }
          searchValue={queryParams.search}
          headerActions={({ isShowingFilters }) =>
            isShowingFilters && (
              <Button
                type="primary"
                onClick={() => setQueryParams({ order: 1 })}
              >
                {intl.formatMessage({ id: 'commons.clear.filters' })}
              </Button>
            )
          }
          extraFilters={
            <WorkOrdersExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />

        {hasWorkorderByWhatsapp && (
          <Row gutter={[16, 16]} justify="space-between">
            <Col span={24} md={12} lg={8}>
              Orçamentos: {workordersSelected.length}
            </Col>

            <Col span={24} md={12} lg={8}>
              <SpaceForButton justifyContent="flex-end">
                <Button block onClick={cancelWorkorderByWhatsapp}>
                  Cancelar envio
                </Button>

                <Button
                  type="primary"
                  block
                  disabled={!workordersSelected.length}
                  onClick={modalCustomerSendAction.open}
                >
                  Enviar ao cliente
                </Button>
              </SpaceForButton>
            </Col>
          </Row>
        )}

        <InfiniteTable<ListWorkOrdersResult>
          loading={isFetching || isFetchingColumns}
          rowKey={(row) => row.complementId}
          dataSource={workOrders}
          columns={columns}
          onChange={(_p, _f, sorter) => {
            if (!Array.isArray(sorter)) {
              workorderOrderColumns(sorter, setQueryParams);
            }
          }}
          showSorterTooltip={false}
          rowSelection={
            hasWorkorderByWhatsapp
              ? {
                  preserveSelectedRowKeys: true,
                  selectedRowKeys: workordersSelected.map(
                    (item) => item.complementId,
                  ),
                  onChange: (_, rows) => {
                    setWorkordersSelected(rows);
                  },
                }
              : undefined
          }
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          scroll={{ x: getTableScrollX(columns) }}
          size="small"
        />
      </DefaultRouteTabs>

      <WorkOrdersByWhatsappModal
        open={modalCustomerSendOpen}
        onCancel={modalCustomerSendAction.close}
        customerWorkorders={customerWorkorders}
      />

      <CreateWorkOrderModal
        visible={createModalVisible}
        handleVisible={handleCreateModalVisible}
      />

      <WorkorderImportModal
        open={modalImportOpen}
        onCancel={modalImportAction.close}
        onImported={() => null}
      />

      <WorkorderCompanySelectModal
        open={modalSelectCompany}
        close={() => {
          modalSelectCompanyAction.close();
          setReportType(undefined);
        }}
        reportType={reportType}
      />

      <WorkOrderChecklistEmptyModal
        open={modalChecklistOpened}
        onCancel={modalChecklistAction.close}
      />
    </>
  );
}

export default WorkOrdersPage;
